import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import {
    AbstractControl,
    ReactiveFormsModule,
    UntypedFormArray,
    UntypedFormBuilder,
    UntypedFormGroup,
} from '@angular/forms';
import { Router } from '@angular/router';
import { MAX_APPLICANTS_PER_HOUSEHOLD } from '@app/settings/settings.const';
import { ApplicantsAwareHousehold } from '@app/interfaces/household.interface';
import { UsersService } from '@app/services/users.service';
import { FormService } from '@app/services/form.service';
import { SnackbarService } from '@app/services/snackbar.service';
import { MemberInterface } from '@app/interfaces/member.interface';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { IncomeTypeEnum } from '@app/enums/income-type.enum';
import { ContactInterface } from '@app/interfaces/contact.interface';
import { Utils } from '@app/utils/utils';
import { Subject } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { HasErrorDirective } from '@app/directives/validation/has-error.directive';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { InfoPanelComponent } from '@app/components/info-panel/info-panel.component';
import { MatButtonModule } from '@angular/material/button';
import { HypoRadioComponent } from '@app/components/form-components/hypo-radio/hypo-radio.component';
import { HypoInputComponent } from '@app/components/form-components/hypo-input/hypo-input.component';
import { YearsPipe } from '@app/pipes/years.pipe';
import { ApplicantNameOrIndexByContactIdPipe } from '@app/pipes/applicant-name-or-index-by-id.pipe';
import { RichFormControlDirective } from '@app/directives/validation/rich-form-control.directive';

@Component({
    selector: 'app-household-dialog',
    templateUrl: './household-dialog.component.html',
    styleUrls: ['./household-dialog.component.scss'],
    imports: [
        MatIconModule,
        HasErrorDirective,
        RichFormControlDirective,
        MatDialogModule,
        MatSlideToggleModule,
        InfoPanelComponent,
        ReactiveFormsModule,
        MatCheckboxModule,
        MatButtonModule,
        HypoRadioComponent,
        HypoInputComponent,
        YearsPipe,
        ApplicantNameOrIndexByContactIdPipe,
    ],
})
export class HouseholdDialogComponent implements OnInit, OnDestroy {
    dialogRef = inject<MatDialogRef<HouseholdDialogComponent>>(MatDialogRef);
    data = inject(MAT_DIALOG_DATA);

    protected readonly usersService = inject(UsersService);
    protected readonly router = inject(Router);
    protected readonly fb = inject(UntypedFormBuilder);
    protected readonly formService = inject(FormService);
    protected readonly snackbarService = inject(SnackbarService);

    protected destroyed$: Subject<void> = new Subject<void>();

    public blurContent = false;
    public MAX_APPLICANTS_PER_HOUSEHOLD = MAX_APPLICANTS_PER_HOUSEHOLD;

    public applicantAloneMarried: boolean = false;
    public applicantAloneRegistered: boolean = false;
    public showContent: boolean = false;

    public household: ApplicantsAwareHousehold;
    public householdForm: UntypedFormGroup;

    public applicants: ContactInterface[] = [];

    public index: number;
    public isSent: boolean = false;
    public householdHasAnyApplicant: boolean = false;

    get householdMembers(): UntypedFormArray {
        return this.householdForm.get('members') as UntypedFormArray;
    }

    public close(data?: any): void {
        this.dialogRef.close(data);
    }

    async ngOnInit() {
        this.household = Utils.cloneDeep(this.data?.household);
        this.applicants = this.data?.applicants;

        if (!ApplicantsAwareHousehold.isApplicantsAwareHousehold(this.household)) {
            [this.household] = ApplicantsAwareHousehold.getEnrichedHouseholds([this.household], this.applicants);
        }

        this.index = this.data?.index;
        this.householdForm = this.formService.createHouseholdForm(this.household);
        this.checkForAlimonyMembers();
        this.checkIfHouseholdHasAnyApplicant();
        this.showContent = true;
        this.blurContent = this.data?.blurContent;
    }

    ngOnDestroy() {
        this.destroyed$?.next();
        this.destroyed$?.complete();
    }

    public removeMember(member: AbstractControl, mIndex) {
        this.householdForm.markAsDirty();
        if (
            member.value.type === 'withoutIncome' &&
            this.checkForAlimonyMembers() &&
            this.householdMembers.value.filter((a) => a.type === 'withoutIncome').length <= 1
        ) {
            this.snackbarService.showError(
                'Bohužel tento člen nelze vymazat, jelikož tato Domácnost obsahuje Žadatele s příjmem "Výživné"'
            );
        } else {
            this.householdMembers.removeAt(mIndex);
        }
    }

    public addMember(member: MemberInterface = {} as MemberInterface) {
        this.householdForm.markAsDirty();
        this.householdMembers.push(this.formService.createMemberForm(member));
    }

    continue() {
        this.isSent = true;
        const householdFormData = this.householdForm.value;
        householdFormData.applicants.sort();
        if (
            this.formService.hasValidVisibleControls(this.householdForm, true) &&
            this.checkIfHouseholdHasAnyApplicant() &&
            this.checkIfApplicantsHaveRightFamilyStatus()
        ) {
            this.close(this.householdForm);
        }
    }

    public applicantCheckboxChange($event: MatCheckboxChange, applicant: ContactInterface) {
        let currentApplicantsValue = this.householdForm.get('applicants').value;
        if ($event.checked) {
            const otherSelectedApplicant = this.applicants.find(
                (currentApplicant) => currentApplicant._id === currentApplicantsValue?.[0]
            );
            if (otherSelectedApplicant) {
                if (this.ifOneHasThisFamilyStatusTheOtherMustMatch(applicant, otherSelectedApplicant, 'married')) {
                    this.snackbarService.showError(
                        'Pokud je jeden ze členů domácnosti ve stavu Ženatý/Vdaná, tak i druhý žadatel musí mít ten stejný Rodinný stav.'
                    );
                    $event.source.checked = false;
                    return;
                } else if (
                    this.ifOneHasThisFamilyStatusTheOtherMustMatch(applicant, otherSelectedApplicant, 'registered')
                ) {
                    this.snackbarService.showError(
                        'Pokud je jeden ze členů domácnosti ve stavu Registrovaný/á partner/ka, tak i druhý žadatel musí mít ten stejný Rodinný stav.'
                    );
                    $event.source.checked = false;
                    return;
                }
            }
            if (currentApplicantsValue.length < MAX_APPLICANTS_PER_HOUSEHOLD) {
                if (currentApplicantsValue.findIndex((a) => a === applicant._id) === -1) {
                    currentApplicantsValue.push(applicant._id);
                    this.checkForAlimonyMembers();
                    if (
                        currentApplicantsValue.length === 1 &&
                        !this.householdMembers.value.length &&
                        applicant.familyStatus === 'marital'
                    ) {
                        this.addMember();
                    }
                }
            } else {
                this.snackbarService.showError(`Maximum členů v jedné domácnosti je ${MAX_APPLICANTS_PER_HOUSEHOLD}`);
                $event.source.checked = false;
                return;
            }
        } else {
            currentApplicantsValue = currentApplicantsValue.filter((a) => a !== applicant._id);
            $event.source.checked = false;
        }
        this.householdForm.get('applicants').patchValue(currentApplicantsValue);
        this.checkIfHouseholdHasAnyApplicant();
        this.checkIfApplicantsHaveRightFamilyStatus();
    }

    public applicantIsChecked(applicant: ContactInterface): boolean {
        let retval: boolean;
        if (this.household) {
            retval = this.household.applicants?.findIndex((applicantId) => applicantId === applicant._id) !== -1;
        } else {
            retval = false;
        }
        return retval;
    }

    private checkIfHouseholdHasAnyApplicant(): boolean {
        this.householdHasAnyApplicant = !!this.householdForm.value.applicants?.length;
        return this.householdHasAnyApplicant;
    }

    private checkIfApplicantsHaveRightFamilyStatus(): boolean {
        this.applicantAloneMarried = false;
        this.applicantAloneRegistered = false;
        const data = this.householdForm.value;
        if (data.applicants?.length === 1) {
            const applicant = this.applicants.find((currentApplicant) => currentApplicant._id === data.applicants[0]);
            if (applicant.familyStatus === 'married') {
                this.applicantAloneMarried = true;
            } else if (applicant.familyStatus === 'registered') {
                this.applicantAloneRegistered = true;
            }
        }
        return !(this.applicantAloneMarried || this.applicantAloneRegistered);
    }

    private checkForAlimonyMembers(): boolean | void {
        const applicantWithAlimony = this.applicants.find((applicant) =>
            applicant.incomes.find((income) => income.type === IncomeTypeEnum.alimony)
        );
        const contactIds = this.householdForm.value.applicants ?? [];
        if (contactIds.length && applicantWithAlimony) {
            const included = contactIds.includes(applicantWithAlimony._id);
            if (included && !this.householdMembers.value.length) {
                this.addMember({ type: 'withoutIncome' } as MemberInterface);
            }
            return included;
        }
    }

    private ifOneHasThisFamilyStatusTheOtherMustMatch(
        applicant1: ContactInterface,
        applicant2: ContactInterface,
        familyStatus: string
    ): boolean {
        return (
            (applicant1.familyStatus === familyStatus && applicant2.familyStatus !== familyStatus) ||
            (applicant2.familyStatus === familyStatus && applicant1.familyStatus !== familyStatus)
        );
    }
}
